/**
 * Place-name normalization for the gazetteer (docs/location-intelligence.md §4.2).
 *
 * Three keys, all computed here so that the seed loader, the database and the resolver agree:
 *  - norm: accents, case, punctuation and a leading type word removed ("Barrio El Porvenir" -> "el porvenir")
 *  - key:  norm without articles/prepositions and without spaces ("el porvenir" -> "porvenir",
 *          "Llano Grande" -> "llanogrande", "Carmen del Viboral" -> "carmenviboral")
 *  - phon: a Spanish (Colombia) phonetic key of `key` (b/v, s/z/c, ll/y, silent h): catches
 *          "Carmen de Biboral" or "Zonzón" without fuzzy matching
 * The text the user wrote is never modified; these keys are only used for matching.
 */

/** Type words a place name may start with, and the kind they announce. Longest first. */
const TYPE_WORDS: Array<[string, TypeHint]> = [
  ['unidad residencial', 'conjunto'],
  ['conjunto residencial', 'conjunto'],
  ['centro poblado', 'centro_poblado'],
  ['urbanizacion', 'urbanizacion'],
  ['urbanizacon', 'urbanizacion'],
  ['corregimiento', 'corregimiento'],
  ['parcelacion', 'urbanizacion'],
  ['conjunto', 'conjunto'],
  ['barrio', 'barrio'],
  ['vereda', 'vereda'],
  ['sector', 'sector'],
  ['correg', 'corregimiento'],
  ['cjto', 'conjunto'],
  ['conj', 'conjunto'],
  ['vrda', 'vereda'],
  ['vda', 'vereda'],
  ['urb', 'urbanizacion'],
  ['br', 'barrio'],
];

/**
 * What the user said the place is (a hint, never a filter). 'address': a street address
 * (nomenclature); 'landmark': a reference ("cerca del aeropuerto") points at a site, not an area.
 */
export type TypeHint = 'barrio' | 'vereda' | 'sector' | 'urbanizacion' | 'conjunto' | 'corregimiento' | 'centro_poblado' | 'address' | 'landmark';

const ARTICLES = new Set(['el', 'la', 'los', 'las', 'de', 'del', 'y']);

/** Lowercase, no accents, no punctuation, single spaces. "El  Peñol!" -> "el penol". */
export function foldText(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** Removes one leading type word ("barrio", "vda.", "urb") and returns it as a hint. */
export function splitTypeWord(folded: string): { rest: string; hint: TypeHint | null } {
  for (const [word, hint] of TYPE_WORDS) {
    if (folded === word) return { rest: '', hint };
    if (folded.startsWith(`${word} `)) return { rest: folded.slice(word.length + 1).trim(), hint };
  }
  return { rest: folded, hint: null };
}

/**
 * Words that name the same kind of site, as people write them: "colegio San José" is the
 * "Institución Educativa San José" (or "I.E. San José") of the catalog. Longest first.
 */
const SITE_SYNONYMS: string[][] = [
  ['institucion educativa', 'colegio', 'escuela', 'i e', 'ie'],
  ['iglesia', 'parroquia', 'templo'],
  ['hospital', 'clinica'],
  // LOC-007: "CC San Nicolás", "C.C. San Nicolás" (folded: "cc", "c c"); "U Católica de Oriente"
  ['centro comercial', 'cc', 'c c'],
  ['universidad', 'u'],
];

/**
 * The same search with its leading site word swapped for each synonym ("colegio san jose" ->
 * "institucion educativa san jose", "escuela san jose", …). Only for searching: stored keys never change.
 */
export function siteSynonymVariants(text: string): string[] {
  const folded = foldText(text);
  for (const group of SITE_SYNONYMS) {
    const word = group.find((w) => folded.startsWith(`${w} `));
    if (!word) continue;
    const rest = folded.slice(word.length + 1);
    return group.filter((w) => w !== word).map((w) => `${w} ${rest}`);
  }
  return [];
}

/** Each synonym of a site word, as one canonical word (multi-word ones first: "institucion educativa"). */
const SITE_CANONICAL: Array<[RegExp, string]> = SITE_SYNONYMS.flatMap((group) =>
  [...group].sort((a, b) => b.length - a.length).map((w): [RegExp, string] => [new RegExp(`(?:^| )${w}(?= |$)`, 'g'), ` ${group[0]!.replace(/ /g, '_')}`]),
);
const SITE_TOKENS: ReadonlySet<string> = new Set(SITE_SYNONYMS.map((g) => g[0]!.replace(/ /g, '_')));

/** The words that tell a place apart: folded, no articles, synonyms of a site word as one word. */
export function significantWords(text: string): string[] {
  let s = foldText(text);
  for (const [re, token] of SITE_CANONICAL) s = s.replace(re, token);
  return s.split(' ').filter((w) => w && !ARTICLES.has(w));
}

/** The words of a text that are not a site word ("roman gomez" of "colegio roman gomez"). */
export function distinctiveWords(text: string): string[] {
  return significantWords(text).filter((w) => !SITE_TOKENS.has(w));
}

/**
 * Every word the user wrote is in the place's name, in any order and with others between them:
 * "iglesia la asuncion" in "Iglesia de Nuestra Señora de la Asunción", "colegio roman gomez" in
 * "Institución Educativa Román Gómez". Returns the share of the name the user wrote, or null when a
 * word is missing or the text has no distinctive word (a bare "iglesia" matches every church).
 */
export function wordsCoverage(query: string, name: string): number | null {
  const q = significantWords(query);
  if (q.length < 2 || !q.some((w) => !SITE_TOKENS.has(w))) return null;
  const n = new Set(significantWords(name));
  if (!q.every((w) => n.has(w))) return null;
  return new Set(q).size / n.size;
}

export function nameNorm(name: string): string {
  const folded = foldText(name);
  const { rest } = splitTypeWord(folded);
  return rest || folded;
}

export function nameKey(name: string): string {
  const norm = nameNorm(name);
  const words = norm.split(' ').filter((w) => w && !ARTICLES.has(w));
  return (words.length ? words : norm.split(' ')).join('');
}

/** Spanish phonetic key. Deliberately small and predictable (see the tests for the equivalences). */
export function namePhon(name: string): string {
  let s = nameKey(name);
  s = s
    .replace(/ch/g, '§')
    .replace(/h/g, '')
    .replace(/§/g, 'ch')
    .replace(/qu/g, 'k')
    .replace(/c(?=[ei])/g, 's')
    .replace(/c/g, 'k')
    .replace(/z/g, 's')
    .replace(/v/g, 'b')
    .replace(/ll/g, 'y')
    .replace(/g(?=[ei])/g, 'j')
    .replace(/y(?![aeiou])/g, 'i')
    .replace(/(.)\1+/g, '$1');
  return s;
}

export interface NameKeys {
  norm: string;
  key: string;
  phon: string;
}

export function nameKeys(name: string): NameKeys {
  return { norm: nameNorm(name), key: nameKey(name), phon: namePhon(name) };
}

/** Levenshtein distance, capped (returns cap + 1 as soon as it is exceeded). */
export function editDistance(a: string, b: string, cap = 3): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const v = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + (a[i - 1] === b[j - 1] ? 0 : 1));
      cur.push(v);
      rowMin = Math.min(rowMin, v);
    }
    if (rowMin > cap) return cap + 1;
    prev = cur;
  }
  return prev[b.length]!;
}

/** Trigram similarity with the same padding rules as pg_trgm (words padded with two leading and one trailing space). */
export function trigramSimilarity(a: string, b: string): number {
  const grams = (s: string) => {
    const out = new Set<string>();
    for (const w of foldText(s).split(' ').filter(Boolean)) {
      const p = `  ${w} `;
      for (let i = 0; i + 3 <= p.length; i++) out.add(p.slice(i, i + 3));
    }
    return out;
  };
  const ga = grams(a);
  const gb = grams(b);
  if (!ga.size || !gb.size) return 0;
  let common = 0;
  for (const g of ga) if (gb.has(g)) common += 1;
  return common / (ga.size + gb.size - common);
}
